import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useCarrito } from "../../lib/CarritoContext.jsx";
import Logo from "../svg/Logo.jsx";
import { EMPRESA } from "../../lib/config";
import EnlaceCotizar from "../ui/EnlaceCotizar.jsx";
import s from "./Header.module.css";

const ENLACES = [
  { to: "/productos", label: "Catalogo" },
  { to: "/servicios", label: "Servicios" },
  { to: "/nosotros", label: "Nosotros" },
  { to: "/contacto", label: "Contacto" },
];

export default function Header() {
  const [abierto, setAbierto] = useState(false);
  const [compacto, setCompacto] = useState(false);
  const { unidades } = useCarrito();
  const { pathname } = useLocation();

  useEffect(() => setAbierto(false), [pathname]);

  /**
   * Estado "compacto" del header. Dos cuidados:
   *  - histeresis (entra a los 90 px, sale a los 40) para que no oscile cuando
   *    el usuario se queda justo en el umbral;
   *  - la lectura del scroll va dentro de un rAF, asi no se mide el layout en
   *    cada evento.
   * La altura de la barra NO cambia, de modo que activarlo no mueve el
   * contenido de la pagina.
   */
  useEffect(() => {
    const ENTRA = 90;
    const SALE = 40;
    let pendiente = false;

    const evaluar = () => {
      pendiente = false;
      const y = window.scrollY;
      setCompacto((previo) => (previo ? y > SALE : y > ENTRA));
    };

    const alScroll = () => {
      if (pendiente) return;
      pendiente = true;
      requestAnimationFrame(evaluar);
    };

    evaluar();
    window.addEventListener("scroll", alScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", alScroll);
      pendiente = true; // evita que un rAF en vuelo toque un componente ya desmontado
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = abierto ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [abierto]);

  return (
    <header className={`${s.header} ${compacto ? s.compacto : ""}`}>
      {/* Franja de utilidad: se va con el scroll, no es pegajosa. */}
      <div className={s.barraTop}>
        <div className={`container ${s.barraTopInner}`}>
          <span className="mono">{EMPRESA.horario}</span>
          <a className={s.barraTopLink} href={`mailto:${EMPRESA.email}`}>
            {EMPRESA.email}
          </a>
        </div>
      </div>

      {/* Barra de navegacion: pegajosa y de altura constante. */}
      <div className={s.barraNav}>
      <div className={`container ${s.inner}`}>
        <Link to="/" className={s.marca} aria-label={`${EMPRESA.nombre} — inicio`}>
          <Logo className={s.logo} />
        </Link>

        <nav className={s.navDesktop} aria-label="Principal">
          {ENLACES.map((e) => (
            <NavLink
              key={e.to}
              to={e.to}
              className={({ isActive }) =>
                `${s.enlace} ${isActive ? s.enlaceActivo : ""}`
              }
            >
              {e.label}
            </NavLink>
          ))}
        </nav>

        <div className={s.acciones}>
          <Link to="/carrito" className={s.carrito} aria-label="Ver carrito">
            <CarritoIcono />
            {unidades > 0 && <span className={s.contador}>{unidades}</span>}
          </Link>

          <EnlaceCotizar className={`btn btn--accent btn--sm ${s.ctaDesktop}`}>
            Cotizar
          </EnlaceCotizar>

          <button
            type="button"
            className={s.hamburguesa}
            aria-expanded={abierto}
            aria-controls="menu-movil"
            onClick={() => setAbierto((v) => !v)}
          >
            <span className="visually-hidden">
              {abierto ? "Cerrar menu" : "Abrir menu"}
            </span>
            <span className={`${s.barra} ${abierto ? s.barraX1 : ""}`} />
            <span className={`${s.barra} ${abierto ? s.barraOculta : ""}`} />
            <span className={`${s.barra} ${abierto ? s.barraX2 : ""}`} />
          </button>
        </div>
      </div>

      <div
        id="menu-movil"
        className={`${s.menuMovil} ${abierto ? s.menuAbierto : ""}`}
        hidden={!abierto}
      >
        <nav className={s.navMovil} aria-label="Principal movil">
          {ENLACES.map((e) => (
            <NavLink
              key={e.to}
              to={e.to}
              className={({ isActive }) =>
                `${s.enlaceMovil} ${isActive ? s.enlaceActivo : ""}`
              }
            >
              {e.label}
            </NavLink>
          ))}
          <EnlaceCotizar className="btn btn--accent btn--block">
            Pedir cotizacion
          </EnlaceCotizar>
        </nav>
      </div>
      </div>
    </header>
  );
}

function CarritoIcono() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <path
        d="M3 4h2.2l2.1 10.4a2 2 0 0 0 2 1.6h7.3a2 2 0 0 0 2-1.5L20.5 7H6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="20" r="1.4" fill="currentColor" />
      <circle cx="17" cy="20" r="1.4" fill="currentColor" />
    </svg>
  );
}
